import {compact, uniqBy} from 'lodash-es'
import {Maybe, Product, ProductVariant} from '~/gql/graphql'
import {PRODUCT_TYPE_QUERYResult} from 'sanity.types'

export type ModelImage = Omit<
    NonNullable<NonNullable<PRODUCT_TYPE_QUERYResult>['images']>[number],
    '_type' | '_key'
>

export const parseProductId = (
    productId: string
): {ideaId: string; sellableId: string; productTypeId: string} => {
    const [ideaId, sellableId] = productId.split(/-(.*)/s) ?? []
    const productTypeId = sellableId?.split('-')[1]

    if (!ideaId || !sellableId || !productTypeId) {
        throw new Error(`Could not parse id '${productId}'`)
    }

    return {ideaId, sellableId, productTypeId}
}

const IMAGE_VP_REGEX = /.*\/products\/[0-9a-zA-Z-]+/
export const getProductImageBaseUrl = (imageSrc?: Maybe<string>): string | undefined => {
    const match = imageSrc?.match(IMAGE_VP_REGEX)
    return match?.[0]
}

export const getCompositionImageBaseUrl = (imageSrc?: Maybe<string>): string | undefined => {
    return getProductImageBaseUrl(imageSrc)?.replace('/products/', '/compositions/')
}

const VP_REGEX = /\/products\/([0-9a-zA-Z-]+)/
export const getVirtualProductId = (imageSrc?: Maybe<string>): string | undefined => {
    const match = imageSrc?.match(VP_REGEX)
    return match?.[1] ?? undefined
}

// right now, there is no better way to find out the default product view
const VIEW_ID_REGEX = /views\/([0-9]+)/
export const getProductDefaultViewId = (imageSrc?: Maybe<string>): string | undefined => {
    return imageSrc?.match(VIEW_ID_REGEX)?.[1]
}

// right now, there is no better way to find out the default product appearance
const APPEARANCE_ID_REGEX = /[?&,]appearanceId=([0-9]+)/
export const getProductDefaultAppearanceId = (url?: Maybe<string>): string | undefined => {
    const appearanceIdMatch = url?.match(APPEARANCE_ID_REGEX)
    return appearanceIdMatch?.[1]
}

// right now, there is no better way to find out the default product appearance
const PRODUCT_TYPE_ID_REGEX = /[?&,]productType[Id]?=([0-9]+)/
export const getProductTypeId = (url?: Maybe<string>): string | undefined => {
    const productTypeIdMatch = url?.match(PRODUCT_TYPE_ID_REGEX)
    return productTypeIdMatch?.[1]
}

// right now, there is no better way to find out the default product appearance
const SELLABLE_ID_REGEX = /[?&,]sellable=([a-zA-Z0-9-]+)/
export const getSellableId = (url?: Maybe<string>): string | undefined => {
    const productTypeIdMatch = url?.match(SELLABLE_ID_REGEX)
    return productTypeIdMatch?.[1]
}

export const getAppearances = (variants: ProductVariant[]) => {
    const appearances = variants?.map((variant) => variant?.appearance)
    return compact(uniqBy(appearances, 'id'))
}

export const filterProductVariants = (
    variants: ProductVariant[],
    filter?: {
        appearanceId?: string | undefined
        sizeId?: string | undefined
    }
): ProductVariant[] => {
    return variants.filter(
        (variant) =>
            (!filter?.sizeId || variant?.size?.id === filter?.sizeId) &&
            (!filter?.appearanceId || variant?.appearance?.id === filter?.appearanceId)
    )
}

export const getProductModelImageSrc = (
    productImageBaseUrl: string,
    viewId: string,
    appearanceId?: string,
    modelId?: string | null,
    crop?: string | null
) => {
    const imageUrl = new URL(`${productImageBaseUrl}/views/${viewId}.webp`)
    appearanceId && imageUrl.searchParams.append('appearanceId', appearanceId)
    modelId && imageUrl.searchParams.append('modelId', modelId)
    crop && imageUrl.searchParams.append('crop', crop)
    return imageUrl.toString()
}

export const getProductModelImageSrcSet = (
    productImageBaseUrl: string,
    viewId: string,
    appearanceId?: string,
    modelId?: string | null,
    crop?: string | null
) => {
    const imageSrc = getProductModelImageSrc(
        productImageBaseUrl,
        viewId,
        appearanceId,
        modelId,
        crop
    )
    const imageUrl = new URL(imageSrc)

    return [100, 500, 1000]
        .map((size) => {
            imageUrl.searchParams.set('width', String(size))
            imageUrl.searchParams.set('height', String(size))
            return `${imageUrl.toString()} ${size}w`
        })
        .join(',')
}

export const getDefaultModelImageSrc = (
    product: Product,
    modelImages: ModelImage[],
    appearanceId?: string | number
): string => {
    const imageSrc = product.image?.src
    const productImageBaseUrl = getProductImageBaseUrl(imageSrc)!
    const viewId = getProductDefaultViewId(imageSrc)!
    const appearanceIdString = String(appearanceId ?? getProductDefaultAppearanceId(imageSrc))

    const firstImage = modelImages.find((image) => image.appearanceId === appearanceIdString)
    return getProductModelImageSrc(
        productImageBaseUrl,
        firstImage?.viewId ?? viewId,
        appearanceIdString,
        firstImage?.modelId,
        firstImage?.crop
    )
}

export const getDesignImageSrc = (
    designImageBaseUrl: string,
    viewId: string,
    appearanceId?: string
) => {
    const imageUrl = new URL(`${designImageBaseUrl}/views/${viewId}.webp`)
    appearanceId && imageUrl.searchParams.append('appearanceId', appearanceId)
    imageUrl.searchParams.append('noPt', 'false')
    return imageUrl.toString()
}

export const getDesignImageSrcSet = (
    designImageBaseUrl: string,
    viewId: string,
    appearanceId?: string
) => {
    const imageSrc = getDesignImageSrc(designImageBaseUrl, viewId, appearanceId)
    const imageUrl = new URL(imageSrc)

    return [100, 500, 750]
        .map((size) => {
            imageUrl.searchParams.set('width', String(size))
            imageUrl.searchParams.set('height', String(size))
            return `${imageUrl.toString()} ${size}w`
        })
        .join(',')
}
